<template>
    <div class="mb-3">
        <div class="section-header" :class="{ open: !collapsed }" @click="collapsed = !collapsed" role="button"
            tabindex="0" @keydown.enter.space.prevent="collapsed = !collapsed" :aria-expanded="!collapsed">
            <div class="section-header-left">
                <span class="section-title">Recherche d’adresse</span>
            </div>
            <span class="section-chevron" :class="{ open: !collapsed }" aria-hidden="true">▾</span>
        </div>

        <div v-if="!collapsed">
            <div class="input-group input-group-sm mb-2">
                <span class="input-group-text" aria-hidden="true">🔎</span>
                <input v-model="q" type="text" class="form-control" placeholder="Paris, Voltaire, 10 rue..."
                    @keydown.enter.prevent="triggerSearch(true)" />
                <button class="btn btn-outline-secondary" type="button" @click="clear"
                    :disabled="!q && !results.length">
                    Effacer
                </button>
            </div>

            <div v-if="loading" class="text-muted small">Recherche…</div>
            <div v-else-if="q && !results.length && didSearch" class="text-muted small">Aucun résultat</div>

            <div v-if="results.length" class="results">
                <button v-for="r in results" :key="r.place_id" type="button" class="result-row" @click="select(r)"
                    :title="r.display_name">
                    <div class="text-truncate fw-semibold">{{ r.mainLabel }}</div>
                    <div class="text-truncate small text-muted">{{ r.subLabel }}</div>
                </button>
            </div>

            <div class="small text-muted mt-2">
                Source : OpenStreetMap (Nominatim)
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "PanelAddressSearch",
    emits: ["select"],
    data() {
        return {
            collapsed: false,
            q: "",
            loading: false,
            didSearch: false,
            results: [],
            searchTimer: null,
            abortController: null,
        };
    },
    watch: {
        q() {
            this.triggerSearch(false);
        },
    },
    beforeUnmount() {
        if (this.searchTimer) clearTimeout(this.searchTimer);
        if (this.abortController) this.abortController.abort();
    },
    methods: {
        clear() {
            this.q = "";
            this.results = [];
            this.didSearch = false;
            this.loading = false;
            if (this.abortController) this.abortController.abort();
        },
        triggerSearch(immediate = false) {
            if (this.searchTimer) clearTimeout(this.searchTimer);

            const term = (this.q || "").trim();
            if (!term || term.length < 3) {
                this.results = [];
                this.didSearch = false;
                this.loading = false;
                if (this.abortController) this.abortController.abort();
                return;
            }

            const delay = immediate ? 0 : 350;
            this.searchTimer = setTimeout(() => this.search(term), delay);
        },
        async search(term) {
            try {
                if (this.abortController) this.abortController.abort();
                this.abortController = new AbortController();

                this.loading = true;
                this.didSearch = true;

                const url = new URL("https://nominatim.openstreetmap.org/search");
                url.searchParams.set("q", term);
                url.searchParams.set("format", "jsonv2");
                url.searchParams.set("limit", "6");
                url.searchParams.set("addressdetails", "1");
                url.searchParams.set("accept-language", "fr");

                const resp = await fetch(url.toString(), {
                    method: "GET",
                    signal: this.abortController.signal,
                    headers: { "Accept": "application/json" },
                });

                const data = await resp.json();
                const arr = Array.isArray(data) ? data : [];

                this.results = arr.map((r) => {
                    const display = (r.display_name || "").toString();
                    const parts = display.split(",").map(s => s.trim()).filter(Boolean);
                    return {
                        ...r,
                        mainLabel: parts.slice(0, 2).join(", "),
                        subLabel: parts.slice(2).join(", "),
                    };
                });
            } catch (e) {
                if (e?.name !== "AbortError") console.error("Nominatim search error:", e);
                this.results = [];
            } finally {
                this.loading = false;
            }
        },
        select(r) {
            const lat = Number(r.lat);
            const lng = Number(r.lon);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

            // boundingbox: [south, north, west, east] (strings)
            const bb = Array.isArray(r.boundingbox) ? r.boundingbox.map(Number) : null;
            const bbox =
                bb && bb.length === 4 && bb.every(Number.isFinite)
                    ? { south: bb[0], north: bb[1], west: bb[2], east: bb[3] }
                    : null;

            this.$emit("select", { lat, lng, bbox, label: r.display_name });
        },
    },
};
</script>

<style scoped>
.section-header {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .75rem;
    min-height: 42px;
    padding: .65rem .8rem;
    margin-bottom: .75rem;
    border: 1px solid rgba(0, 0, 0, .08);
    border-radius: .75rem;
    background: #f8f9fa;
    color: #495057;
    transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
    user-select: none;
}

.section-header:hover,
.section-header:focus {
    background: #eef3f8;
    border-color: rgba(13, 110, 253, .25);
    box-shadow: 0 0 0 3px rgba(13, 110, 253, .08);
    outline: none;
}

.section-header.open {
    background: #eef4ff;
    border-color: rgba(13, 110, 253, .28);
}

.section-header-left {
    display: flex;
    align-items: center;
    min-width: 0;
}

.section-title {
    font-size: .9rem;
    font-weight: 600;
    color: #212529;
}

.section-chevron {
    flex: 0 0 auto;
    width: 2rem;
    height: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: rgba(0, 0, 0, .05);
    color: #495057;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1;
    transform: rotate(-90deg);
    transition: transform .18s ease, background-color .18s ease, color .18s ease;
}

.section-chevron.open {
    transform: rotate(0deg);
    background: rgba(13, 110, 253, .12);
    color: #0d6efd;
}

.results {
    display: flex;
    flex-direction: column;
    gap: .35rem;
}

.result-row {
    text-align: left;
    border: 1px solid rgba(0, 0, 0, .08);
    background: #fff;
    border-radius: .5rem;
    padding: .5rem .6rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .05);
}

.result-row:hover,
.result-row:focus {
    border-color: rgba(13, 110, 253, .4);
    box-shadow: 0 6px 16px rgba(13, 110, 253, .12);
}
</style>
